import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ElMessage } from 'element-plus';
import router from '@/router';
import { resetTokenValidated } from '@/utils/authState';
import type { ApiResponse } from '@/types';

// 扩展 AxiosRequestConfig 支持 silent 静默模式
declare module 'axios' {
  interface AxiosRequestConfig {
    silent?: boolean;
  }
}

// 创建 axios 实例
const service: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response;
    
    // 业务状态码判断
    if (data.code === 200 || data.code === 0) {
      return data.data as unknown as AxiosResponse;
    }
    
    // 业务错误
    if (!response.config?.silent) {
      ElMessage.error(data.message || '请求失败');
    }
    return Promise.reject(new Error(data.message || '请求失败'));
  },
  (error) => {
    const { response, config } = error;
    
    // 静默模式 - 不弹错误提示
    if (config?.silent) {
      return Promise.reject(error);
    }
    
    if (response) {
      const { status, data } = response;
      
      switch (status) {
        case 401:
          ElMessage.error('登录已过期，请重新登录');
          localStorage.removeItem('token');
          // 会话中途失效：重置校验标志，使重新登录后能再次向后端校验
          resetTokenValidated();
          // 跳转登录页
          if (window.location.pathname !== '/login') {
            router.push({ path: '/login', query: { redirect: window.location.pathname } });
          }
          break;
        case 403:
          ElMessage.error('没有权限，请重新登录');
          localStorage.removeItem('token');
          // 会话中途失效：重置校验标志，使重新登录后能再次向后端校验
          resetTokenValidated();
          // 跳转登录页
          if (window.location.pathname !== '/login') {
            router.push({ path: '/login', query: { redirect: window.location.pathname } });
          }
          break;
        case 404:
          ElMessage.error('请求的资源不存在');
          break;
        case 422:
          ElMessage.error(data?.message || '参数验证失败');
          break;
        case 500:
          // 优先显示后端返回的详细错误信息
          ElMessage.error(data?.message || '服务器错误，请稍后重试');
          break;
        default:
          ElMessage.error(data?.message || '请求失败');
      }
    } else {
      ElMessage.error('网络连接失败，请检查网络');
    }
    
    return Promise.reject(error);
  }
);

// 封装请求方法
export const request = {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.get(url, config) as Promise<T>;
  },
  
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return service.post(url, data, config) as Promise<T>;
  },
  
  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return service.put(url, data, config) as Promise<T>;
  },
  
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.delete(url, config) as Promise<T>;
  },
  
  // 文件下载
  download(url: string, filename: string, config?: AxiosRequestConfig): Promise<void> {
    return service.get(url, {
      ...config,
      responseType: 'blob'
    }).then((response: unknown) => {
      const blob = new Blob([response as BlobPart]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    });
  }
};

export default service;
